import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationAdminService, EmailTemplate } from '../../../shared/services/notification-admin.service';

@Component({
  selector: 'app-email-designer',
  templateUrl: './email-designer.component.html',
  styleUrls: ['./email-designer.component.css'],
  standalone: false
})
export class EmailDesignerComponent implements OnInit, AfterViewInit {
  @ViewChild('emailEditor') emailEditor: any;

  templateId: number | null = null;
  template: EmailTemplate | null = null;
  loading = false;
  saving = false;
  isNew = false;

  // Template types: 'designer' | 'html' | 'text'
  selectedTemplateType: 'designer' | 'html' | 'text' = 'designer';
  
  // The template content (JSON for designer, HTML string for html/text)
  selectedTemplate: any = null;

  // Template metadata
  templateName = '';
  templateSubject = '';
  templateEventType = '';
  isActive = true;

  eventTypes: string[] = [];

  // Available template variables by event type
  availableVariables: { [key: string]: string[] } = {
    Common: ['UserName', 'UserEmail', 'CurrentDate', 'AppUrl'],
    OrderCreated: ['OrderId', 'OrderType', 'Reference', 'Priority', 'Requestor', 'RequiredDate'],
    OrderSubmitted: ['OrderId', 'OrderType', 'Reference', 'Priority', 'Requestor', 'SubmittedBy', 'SubmittedDate'],
    OrderApproved: ['OrderId', 'ApprovedBy', 'ApprovalDate', 'Comments'],
    OrderRejected: ['OrderId', 'RejectedBy', 'RejectionDate', 'Comments'],
    OrderCompleted: ['OrderId', 'CompletedDate'],
    TaskAssigned: ['TaskId', 'TaskName', 'AssignedTo', 'DueDate', 'Priority'],
    TaskPickedUp: ['TaskId', 'TaskName', 'PickedUpBy', 'PickupTime'],
    TaskStatusChanged: ['TaskId', 'TaskName', 'OldStatus', 'NewStatus'],
    CutterOverrideRequested: ['OrderId', 'RequestedBy', 'CutterFamily', 'Justification', 'ApproveUrl', 'DenyUrl'],
    CutterOverrideApproved: ['OrderId', 'ApprovedBy', 'ApprovalCode', 'ExpirationDate'],
    CutterOverrideDenied: ['OrderId', 'DeniedBy', 'Reason'],
    FileUploaded: ['OrderId', 'FileName', 'UploadedBy'],
    CommentAdded: ['OrderId', 'CommentBy', 'CommentText'],
    PriorityChanged: ['OrderId', 'OldPriority', 'NewPriority', 'ChangedBy']
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notificationAdminService: NotificationAdminService,
    private notificationService: NotificationService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.loadEventTypes();
    
    // Check if we're editing an existing template
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.templateId = +params['id'];
        this.loadTemplate(this.templateId);
      } else {
        this.isNew = true;
        this.initNewTemplate();
      }
    });
  }

  ngAfterViewInit(): void {
    // Initialize editor after view is ready
  }

  loadEventTypes(): void {
    this.notificationAdminService.getEventTypes().subscribe({
      next: (types) => {
        this.eventTypes = types;
      },
      error: (err) => {
        console.error('Failed to load event types', err);
      }
    });
  }

  loadTemplate(id: number): void {
    this.loading = true;
    this.notificationAdminService.getTemplate(id).subscribe({
      next: (template) => {
        this.template = template;
        this.templateName = template.name;
        this.templateSubject = template.subject;
        this.templateEventType = template.eventType;
        this.isActive = template.isActive;
        
        // Parse the template body - check if it's JSON (designer format) or HTML
        try {
          const parsed = JSON.parse(template.body);
          if (parsed && parsed.templateType) {
            this.selectedTemplateType = parsed.templateType;
            this.selectedTemplate = parsed.content;
          } else {
            // Legacy HTML template
            this.selectedTemplateType = 'html';
            this.selectedTemplate = template.body;
          }
        } catch {
          // Plain HTML or text template
          this.selectedTemplateType = 'html';
          this.selectedTemplate = template.body;
        }
        
        this.loading = false;
      },
      error: (err) => {
        this.showError('Failed to load template');
        this.loading = false;
        this.router.navigate(['/admin/email-templates']);
      }
    });
  }

  initNewTemplate(): void {
    this.templateName = '';
    this.templateSubject = '';
    this.templateEventType = '';
    this.isActive = true;
    this.selectedTemplateType = 'designer';
    this.selectedTemplate = null;
  }

  onTemplateTypeChange(type: 'designer' | 'html' | 'text'): void {
    if (this.selectedTemplate && type !== this.selectedTemplateType) {
      // Warn user about losing content
      const dialog = this.dialogService.open({
        title: 'Change Template Type',
        content: 'Changing the template type will reset the current content. Are you sure?',
        actions: [
          { text: 'Cancel' },
          { text: 'Continue', themeColor: 'primary' }
        ],
        width: 400
      });

      dialog.result.subscribe((result: any) => {
        if (result.text === 'Continue') {
          this.selectedTemplateType = type;
          this.selectedTemplate = null;
        }
      });
    } else {
      this.selectedTemplateType = type;
    }
  }

  // Called when the email designer content changes
  onEmailContentChange(event: any): void {
    this.selectedTemplate = event;
  }

  // Handle image selection from the designer
  onImageSelectionTrigger(event: any): void {
    // Implement your image selection logic here
    // This could open a file picker or an image library dialog
    console.log('Image selection triggered', event);
    
    // Example: Open file picker
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadImage(file, event);
      }
    };
    input.click();
  }

  // Handle image upload from the designer
  onImageUploadTrigger(event: any): void {
    // Implement your image upload logic here
    console.log('Image upload triggered', event);
    
    if (event.file) {
      this.uploadImage(event.file, event);
    }
  }

  private uploadImage(file: File, event: any): void {
    // TODO: Implement actual image upload to your server/cloud storage
    // For now, convert to base64 as a placeholder
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const imageUrl = e.target.result;
      
      // Call the callback provided by the designer with the image URL
      if (event.callback) {
        event.callback(imageUrl);
      }
    };
    reader.readAsDataURL(file);
  }

  getVariablesForEventType(): string[] {
    const common = this.availableVariables['Common'] || [];
    const eventSpecific = this.availableVariables[this.templateEventType] || [];
    return [...common, ...eventSpecific];
  }

  insertVariable(variable: string): void {
    // Insert variable into subject field at cursor position
    const variableText = `{{${variable}}}`;
    this.templateSubject += variableText;
  }

  async save(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    this.saving = true;

    try {
      // Get the HTML output from the designer
      let htmlContent = '';
      let bodyToSave = '';

      if (this.selectedTemplateType === 'designer' && this.emailEditor) {
        // Get HTML from the designer component
        htmlContent = await this.getHtmlFromDesigner();
        
        // Save both the designer JSON and the rendered HTML
        bodyToSave = JSON.stringify({
          templateType: 'designer',
          content: this.selectedTemplate,
          renderedHtml: htmlContent
        });
      } else if (this.selectedTemplateType === 'html') {
        bodyToSave = this.selectedTemplate || '';
      } else {
        // Plain text
        bodyToSave = this.selectedTemplate || '';
      }

      const templateData: Partial<EmailTemplate> = {
        name: this.templateName,
        subject: this.templateSubject,
        eventType: this.templateEventType,
        body: bodyToSave,
        isActive: this.isActive
      };

      if (this.isNew) {
        this.notificationAdminService.createTemplate(templateData as EmailTemplate).subscribe({
          next: (result) => {
            this.showSuccess('Template created successfully');
            this.router.navigate(['/admin/email-templates']);
          },
          error: (err) => {
            this.showError('Failed to create template');
            this.saving = false;
          }
        });
      } else {
        templateData.id = this.templateId!;
        this.notificationAdminService.updateTemplate(this.templateId!, templateData as EmailTemplate).subscribe({
          next: (result) => {
            this.showSuccess('Template saved successfully');
            this.saving = false;
          },
          error: (err) => {
            this.showError('Failed to save template');
            this.saving = false;
          }
        });
      }
    } catch (err) {
      this.showError('Failed to save template');
      this.saving = false;
    }
  }

  private async getHtmlFromDesigner(): Promise<string> {
    // The email designer component should expose a method to get HTML
    // This depends on the exact API of @send-with-ses/ng-email-designer
    if (this.emailEditor && this.emailEditor.getHtml) {
      return await this.emailEditor.getHtml();
    }
    return '';
  }

  private validateForm(): boolean {
    if (!this.templateName.trim()) {
      this.showError('Please enter a template name');
      return false;
    }
    if (!this.templateEventType) {
      this.showError('Please select an event type');
      return false;
    }
    if (!this.templateSubject.trim()) {
      this.showError('Please enter a subject');
      return false;
    }
    return true;
  }

  cancel(): void {
    this.router.navigate(['/admin/email-templates']);
  }

  preview(): void {
    // Open preview in a new window/dialog
    const dialog = this.dialogService.open({
      title: 'Email Preview',
      content: 'Preview functionality - implement based on your needs',
      width: 800,
      height: 600
    });
  }

  sendTestEmail(): void {
    const dialog = this.dialogService.open({
      title: 'Send Test Email',
      content: 'Enter an email address to send a test email',
      width: 400
    });
  }

  private showSuccess(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'success', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 300 }
    });
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      type: { style: 'error', icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 300 }
    });
  }
}
